import { BaseService } from '../base/BaseService';

import { resolveOwnerId } from '@/lib/identity/resolveOwnerId';

/** One sentence out of the material a derived Persona was actually built from. */
export interface PersonaEvidenceItem {
  kind?: string | null;
  quote?: string | null;
}

export interface BrandAudience {
  id: string;
  name: string;
  who: string;
  desire: string | null;
  painLanguage: string | null;
  objection: string | null;
  source: 'derived' | 'user' | 'imported';
  status: 'active' | 'proposed' | 'retired';
  updatedAt: string;
  reuseBriefCount: number;
  /**
   * The source sentences a `derived` Persona was actually built from. Stored
   * since the table shipped (`persist-research.ts` writes them as
   * `{kind:'source_claim', quote}`) and, until 2026-08-27, selected by no read
   * path at all — so the `Researched` badge was a claim nobody could check.
   */
  evidence: PersonaEvidenceItem[];
}

export interface SaveManualPersonaInput {
  /** The workspace this Persona belongs to. Empty keeps it visible in every workspace. */
  brandKitId?: string | null;
  /**
   * Generated client-side at dialog open so the Persona's identity mark can be
   * previewed while it is being typed and still be the mark that gets saved.
   * `PersonaMark` seeds on the id, so a preview seeded on anything else would
   * show a figure the saved row does not have.
   */
  id?: string;
  name: string;
  who: string;
  desire?: string | null;
  painLanguage?: string | null;
  objection?: string | null;
}

export interface UpdateManualPersonaInput extends SaveManualPersonaInput {
  id: string;
  expectedUpdatedAt: string;
}

type AudienceRow = {
  id: string;
  name: string;
  who: string;
  desire: string | null;
  pain_language: string | null;
  objection: string | null;
  source: 'derived' | 'user' | 'imported';
  status: 'active' | 'proposed' | 'retired';
  updated_at: string;
  evidence: unknown;
};

const PERSONA_SELECT =
  'id, name, who, desire, pain_language, objection, source, status, updated_at, evidence';

function clean(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

/** `evidence` is a jsonb column, so anything could be in it. Only well-formed
 *  `{quote}` objects survive — a malformed row shows no sources rather than a
 *  blank bullet. */
export function readPersonaEvidence(value: unknown): PersonaEvidenceItem[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    if (!item || typeof item !== 'object') return [];
    const quote = (item as { quote?: unknown }).quote;
    if (typeof quote !== 'string' || quote.trim().length === 0) return [];
    const kind = (item as { kind?: unknown }).kind;
    return [{ kind: typeof kind === 'string' ? kind : null, quote }];
  });
}

function mapAudience(row: AudienceRow, reuseBriefCount = 0): BrandAudience {
  return {
    id: row.id,
    name: row.name,
    who: row.who,
    desire: row.desire,
    painLanguage: row.pain_language,
    objection: row.objection,
    source: row.source,
    status: row.status,
    updatedAt: row.updated_at,
    reuseBriefCount,
    evidence: readPersonaEvidence(row.evidence),
  };
}

/** Account-scoped Persona library. Studio keeps its lightweight consumer hook. */
export class BrandAudienceService extends BaseService {
  /**
   * With a workspace, returns its Personas plus any not tied to one workspace.
   * Without one, returns every Persona on the account.
   */
  async listForCurrentAccount(brandKitId?: string | null): Promise<BrandAudience[]> {
    const actorId = await this.ensureAuthenticated();
    return this.list(await resolveOwnerId(actorId), brandKitId);
  }

  async list(ownerId: string, brandKitId?: string | null): Promise<BrandAudience[]> {
    let audienceQuery = this.client
      .from('brand_audiences')
      .select(PERSONA_SELECT)
      .eq('user_id', ownerId)
      .in('status', ['active', 'proposed']);
    if (brandKitId) {
      audienceQuery = audienceQuery.or(`brand_kit_id.is.null,brand_kit_id.eq.${brandKitId}`);
    }
    const [audienceResult, usageResult] = await Promise.all([
      audienceQuery.order('updated_at', { ascending: false }),
      this.client
        .from('project_audience_refs')
        .select('audience_id, projects!inner(kind)')
        .eq('user_id', ownerId)
        .eq('projects.kind', 'brief'),
    ]);

    if (audienceResult.error) throw audienceResult.error;
    if (usageResult.error) throw usageResult.error;

    const counts = new Map<string, number>();
    for (const row of (usageResult.data ?? []) as Array<{ audience_id: string }>) {
      counts.set(row.audience_id, (counts.get(row.audience_id) ?? 0) + 1);
    }

    return (audienceResult.data ?? []).map((row) =>
      mapAudience(row as AudienceRow, counts.get(row.id) ?? 0),
    );
  }

  async createManual(input: SaveManualPersonaInput): Promise<BrandAudience> {
    const actorId = await this.ensureAuthenticated();
    const ownerId = await resolveOwnerId(actorId);
    const { data, error } = await this.client
      .from('brand_audiences')
      .insert({
        ...(input.id ? { id: input.id } : {}),
        user_id: ownerId,
        name: input.name.trim(),
        who: input.who.trim(),
        desire: clean(input.desire),
        pain_language: clean(input.painLanguage),
        objection: clean(input.objection),
        source: 'user',
        status: 'active',
        evidence: [],
        ...(input.brandKitId ? { brand_kit_id: input.brandKitId } : {}),
      } as never)
      .select(PERSONA_SELECT)
      .single();

    if (error) throw error;
    return mapAudience(data as AudienceRow);
  }

  async updateManual(input: UpdateManualPersonaInput): Promise<BrandAudience> {
    const actorId = await this.ensureAuthenticated();
    const ownerId = await resolveOwnerId(actorId);
    const { data, error } = await this.client
      .from('brand_audiences')
      .update({
        name: input.name.trim(),
        who: input.who.trim(),
        desire: clean(input.desire),
        pain_language: clean(input.painLanguage),
        objection: clean(input.objection),
      })
      .eq('id', input.id)
      .eq('user_id', ownerId)
      .eq('source', 'user')
      .eq('updated_at', input.expectedUpdatedAt)
      .select(PERSONA_SELECT)
      .maybeSingle();

    if (error) throw error;
    if (!data) {
      throw Object.assign(new Error('This Persona changed elsewhere. Reload and try again.'), {
        code: 'persona_updated_conflict',
      });
    }
    return mapAudience(data as AudienceRow);
  }

  async setArchived(id: string, archived: boolean): Promise<void> {
    const actorId = await this.ensureAuthenticated();
    const ownerId = await resolveOwnerId(actorId);
    const { data, error } = await this.client
      .from('brand_audiences')
      .update({ status: archived ? 'retired' : 'active' })
      .eq('id', id)
      .eq('user_id', ownerId)
      .eq('source', 'user')
      .select('id')
      .maybeSingle();

    if (error) throw error;
    if (!data) throw new Error('This Persona could not be found.');
  }
}

export const brandAudienceService = new BrandAudienceService();
